import { Optional } from '@angular/core';
import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {IAvailableTypes} from 'src/app/core/models/available-types.interface';
import {ConstantService} from 'src/app/core/services/constant.service';
import {NotificationService} from 'src/app/core/services/notification.service';
import {UserService} from 'src/app/core/services/user.service';
import {SaleOrderStatus} from 'src/app/shared/enums/sale-order-status.enum';
import {ReorderPopupDialog} from 'src/app/shared/popups/reorder-popup/reorder-popup.component';
import {ISaleOrder} from '../../models/sale-order.interface';
import {SaleOrderService} from '../../services/sale-order.service';
import { TypeBusinesses } from '../../../../shared/enums/type-business';
import { Roles, UserRoles } from 'src/app/shared/enums/role-types.enum';
import { AccountType } from 'src/app/shared/enums/account-type';
import { formatGrossOrderTotal } from '../../utils/sale-order-list-net-amounts.util';
import { DeliveryStatusLabelPipe } from 'src/app/shared/pipes/delivery-status-label.pipe';
import { TaxReportType } from 'src/app/shared/enums/tax-report-type.enum';

@Component({
    selector: 'eci-sale-order-details',
    templateUrl: './sale-order-details.component.html',
    styleUrls: ['./sale-order-details.component.scss']
})
export class SaleOrderDetailsComponent implements OnInit {

    createMode: boolean = false;
    isB2BSaleOrder: boolean = false;
    isMarketplaceSO: boolean = false;
    editMode: boolean = true;
    marginTop: string;
    info: ISaleOrder;
    availableTypes: IAvailableTypes;
    currentOrderId: number;
    availableStates: string[];
    statusChanged: boolean = false;
    header: string = 'Sales Management';
    selectedTab: number = 0;
    showLoader: boolean = false;
    orderType: number;
    showErrors: boolean = false;

    TypeBusinesses = TypeBusinesses;

    errorPanelOpenState: boolean = false;

    selectedShop: any;
    originCountry: any;
    errors = [
        true,
        true,
    ];

    get isAdmin(): boolean {
        return this.userSvc.isAdmin;
    }

    get soErrors(): number[] {
        if (!this.info || !this.info.errors) {
          return [];
        }
        let arr = this.info?.errors?.split(',');
        if (!arr) {
            return [];
        }
        let arrNum = arr.map(el => Number(el));
        return arrNum;
    }

    // for test

    get isReorderAvailable(): boolean {
        return this?.info?.status !== SaleOrderStatus.CANCELED;
    }

    get isShowOutboundLogs(): boolean {
        return !this.createMode &&
            (this.userSvc.isAdmin || this.userSvc.isPartnerAccount) &&
            !!this.info && !!this.info?.company?.users &&
            this.info?.company?.accountType === AccountType.Partner;
    }

    get hasRefunds(): boolean {
        if (this.createMode || !this.info || this.info.refundType == null) {
            return false;
        }
        const orderType = this.info.orderType;
        return orderType == null || orderType === 1;
    }

    get displayEmpactSaleOrderTotal(): string {
        if (!this.info) {
            return (0).toFixed(2);
        }
        return formatGrossOrderTotal(this.info);
    }

    formatHistoryStatusLabel(statusId: number | null | undefined): string {
        if (statusId == null) {
            return '—';
        }
        return this.deliveryStatusLabelPipe.transform(statusId) || '—';
    }

    formatReportTypeLabel(reportType: number | undefined): string {
        if (reportType == null) {
            return '';
        }
        const fromConstants = this.availableTypes?.TaxReportType?.find((item) => item.id === reportType)?.name;
        if (fromConstants) {
            return fromConstants;
        }
        return TaxReportType[reportType] ?? String(reportType);
    }

    constructor(
        public userSvc: UserService,
        private constSvc: ConstantService,
        private route: ActivatedRoute,
        public dialogRef: MatDialogRef<SaleOrderDetailsComponent>,
        private notify: NotificationService,
        private router: Router,
        private saleOrderSvc: SaleOrderService,
        private dialog: MatDialog,
        private deliveryStatusLabelPipe: DeliveryStatusLabelPipe,
        @Optional()
        @Inject(MAT_DIALOG_DATA) public dialogData: {disableB2BShipment: boolean}
    ) {
    }

    ngOnInit(): void {
        this.getAvailableTypes();
        if (!this.info?.isDraft) {
            this.getInfo();
        }
    }

    getAvailableTypes(): void {
        this.constSvc.availableTypes.subscribe((data) => {
            this.availableTypes = data;
        });
    }

    getInfo(): void {
        this.route.data.subscribe(data => {
            this.info = data['data'];
            // commented this code because we are not using it anymore and not sure why we set the eciTotal and total to 0 when the discount is 100
            // if (this.info?.products[0]?.discount === 100) {
            //     this.info.eciTotal = 0;
            //     this.info.total = 0;
            // }
            // if (!this.createMode) {
            //     this.getAvailableStates(this.info.id);
            // } // Commented because we are not using it anymore to fetch the next state of sale order. Keeping it in case we need it in the future.
        });
    }

    public save(): void {
        if (!this.isValid()) {
            return;
        }
        const methodName = this.info?.isDraft ? 'updateDraftOrder' : 'createOrder';
        this.info.isEmpact = this.isMarketplaceSO;
        this.info.total = this.info.total || 0;
        this.showLoader = true;

        if (this.createMode) {
            this.saleOrderSvc[methodName](this.info)
                .subscribe(data => {
                    this.showLoader = false;
                    this.dialogRef.close();
                    this.router.navigate([`sales-order-management/order-details/${data.id}`]);
                }, (err) => {
                    this.notify.showError(err?.error);
                    this.showLoader = false;
                });
        }
    }

    saveDraft(): void {
        if (!this.isValid()) {
            return;
        }
        this.showLoader = true;
        if (this.createMode) {
            this.saleOrderSvc.createDraftOrder(this.info).subscribe((data) => {
                this.dialogRef.close();
            }, error => {
                this.showLoader = false;
                this.notify.showError(error?.error ? error.error : 'Something went wrong');
            });
        }
    }

    get canSave(): boolean {
        return !this.errors.find(el => el);
    }

    isValid() {
        this.showErrors = true;

        if (!this.canSave) {
            const Tab = this.errors.findIndex(el => el);
            if (this.selectedTab == Tab) {
                this.notify.showError('Not all fields are filled');
            }
            this.selectedTab = Tab;
            return false;
        }
        return true;
    }

    get checkMin(): boolean {
        return this.info.marketplace.vatCharges >= this.info.total;
    }

    // getAvailableStates(orderId): void {
    //     this.saleOrderSvc.getAvailableOrderStates(orderId).subscribe(data => {
    //         if (!data) {
    //             return;
    //         }
    //         this.availableStates = data;
    //     });
    // } // Commented because we are not using it anymore to fetch the next state of sale order. Keeping it in case we need it in the future.

    getOrderById(orderId: number): void {
        this.saleOrderSvc.getOrderById(orderId).subscribe((data: ISaleOrder) => {
            if (!data) {
                return;
            }
            this.info = data;
        });
    }

    reload(): void {
        if (!this.info) {
            return;
        }
        this.getOrderById(this.info?.id);
    }

    reorder(): void {
        if (!this.info) {
            return;
        }
        const dialogRef = this.dialog.open(ReorderPopupDialog);
        dialogRef.componentInstance.isB2BSaleOrder = !!this.info.isB2B;

        dialogRef.afterClosed().subscribe(value => {
            this.info.shipmentTrackingNumber = value?.trackingNumber || undefined;
            this.info.marketplace.marketplaceOrderId = value?.saleOrderId || undefined;
            this.info.marketplace.referenceOrderId = value?.referenceOrderId || undefined;

            if (!value) {
                return;
            }
            this.saleOrderSvc.reorder(this.info?.id, this.info).subscribe(value => {
                if (!value) {
                    return;
                }
                this.info = value;
                this.notify.showSuccessMessage("Reorder created successfully");
            }, err => {
                this.notify.showError(err?.error?.message);
            });
        })
    }

    navigateToShipment(id): void {
        if (!id) {
            return;
        }
        this.router.navigate([`customers-order/order-details/${id}`])
    }
}
